import { getRefId } from "../location/installation-ids";

/**
 * Search params for focusing a floor, zone, or sensor on the location map.
 * Location is carried by the path (`/organization/locations/:locationId`);
 * these params refine selection after `view=map`.
 */
export const LOCATION_MAP_FOCUS_PARAMS = {
	FLOOR: "floor",
	SENSOR: "sensor",
	ZONE: "zone",
} as const;

export type LocationMapFocus = {
	floorId?: null | string;
	sensorId?: null | string;
	zoneId?: null | string;
};

export type LocationMapFocusEntities = {
	floors: ReadonlyArray<{ id: string }>;
	zones: ReadonlyArray<{
		floorId?: null | string;
		id: string;
		sensorIds: ReadonlyArray<string>;
	}>;
};

export type ResolvedLocationMapFocus = {
	floorId: null | string;
	sensorId: null | string;
	zoneId: null | string;
};

const readFocusParam = (
	searchParams: URLSearchParams,
	key: string,
): null | string => {
	const value = searchParams.get(key);
	return value && value.length > 0 ? value : null;
};

/** Read floor/zone/sensor map focus from the current URL search params. */
export const parseLocationMapFocus = (
	searchParams: URLSearchParams,
): ResolvedLocationMapFocus => ({
	floorId: readFocusParam(searchParams, LOCATION_MAP_FOCUS_PARAMS.FLOOR),
	sensorId: readFocusParam(searchParams, LOCATION_MAP_FOCUS_PARAMS.SENSOR),
	zoneId: readFocusParam(searchParams, LOCATION_MAP_FOCUS_PARAMS.ZONE),
});

/**
 * Apply or clear floor/zone/sensor map focus on a copy of the current search
 * params. Preserves unrelated params (e.g. `view`).
 */
export const applyLocationMapFocus = (
	current: URLSearchParams,
	focus: LocationMapFocus,
): URLSearchParams => {
	const next = new URLSearchParams(current);
	next.delete(LOCATION_MAP_FOCUS_PARAMS.FLOOR);
	next.delete(LOCATION_MAP_FOCUS_PARAMS.ZONE);
	next.delete(LOCATION_MAP_FOCUS_PARAMS.SENSOR);

	if (focus.floorId) next.set(LOCATION_MAP_FOCUS_PARAMS.FLOOR, focus.floorId);
	if (focus.zoneId) next.set(LOCATION_MAP_FOCUS_PARAMS.ZONE, focus.zoneId);
	if (focus.sensorId) {
		next.set(LOCATION_MAP_FOCUS_PARAMS.SENSOR, focus.sensorId);
	}

	return next;
};

export const toLocationMapFocusEntities = (
	locations: ReadonlyArray<{
		floors?: null | ReadonlyArray<{ id: string }>;
		zones?: null | ReadonlyArray<{
			floor?: unknown;
			id: string;
			sensors?: null | ReadonlyArray<{ id: string }>;
		}>;
	}>,
): LocationMapFocusEntities => ({
	floors: locations.flatMap((location) => location.floors ?? []),
	zones: locations.flatMap((location) =>
		(location.zones ?? []).map((zone) => ({
			floorId: getRefId(zone.floor) ?? null,
			id: zone.id,
			sensorIds: (zone.sensors ?? []).map((sensor) => sensor.id),
		})),
	),
});

const floorIdForZone = (
	zone: { floorId?: null | string },
	floorIds: ReadonlySet<string>,
): null | string =>
	zone.floorId && floorIds.has(zone.floorId) ? zone.floorId : null;

/**
 * Keep the most specific id that still exists and derive its parents from
 * current data. A sensor that moved zones replaces the URL zone and floor.
 * A zone that moved floors replaces the URL floor. Ids that match nothing
 * are dropped.
 */
export const resolveLocationMapFocus = (
	focus: LocationMapFocus,
	entities: LocationMapFocusEntities,
): ResolvedLocationMapFocus => {
	const floorIds = new Set(entities.floors.map((floor) => floor.id));
	const zoneById = new Map(entities.zones.map((zone) => [zone.id, zone]));
	const zoneBySensorId = new Map<string, (typeof entities.zones)[number]>();
	for (const zone of entities.zones) {
		for (const sensorId of zone.sensorIds) {
			zoneBySensorId.set(sensorId, zone);
		}
	}

	const sensorZone = focus.sensorId
		? zoneBySensorId.get(focus.sensorId)
		: undefined;
	if (focus.sensorId && sensorZone) {
		return {
			floorId: floorIdForZone(sensorZone, floorIds),
			sensorId: focus.sensorId,
			zoneId: sensorZone.id,
		};
	}

	const zone = focus.zoneId ? zoneById.get(focus.zoneId) : undefined;
	if (zone) {
		return {
			floorId: floorIdForZone(zone, floorIds),
			sensorId: null,
			zoneId: zone.id,
		};
	}

	if (focus.floorId && floorIds.has(focus.floorId)) {
		return {
			floorId: focus.floorId,
			sensorId: null,
			zoneId: null,
		};
	}

	return { floorId: null, sensorId: null, zoneId: null };
};

export const locationMapFocusMatches = (
	left: LocationMapFocus,
	right: LocationMapFocus,
): boolean =>
	(left.floorId || null) === (right.floorId || null) &&
	(left.sensorId || null) === (right.sensorId || null) &&
	(left.zoneId || null) === (right.zoneId || null);

export const locationMapFocusKey = (
	locationId: string,
	focus: LocationMapFocus,
): string =>
	`${locationId}:${focus.floorId ?? ""}:${focus.zoneId ?? ""}:${focus.sensorId ?? ""}`;
